<script setup lang="ts">
// 消息流容器：滚动跟随 + 空态提示
//
// 滚动策略：只在用户已经贴底时才自动跟随（向上翻阅历史时不被增量内容拽回底部）。
import { nextTick, onMounted, ref, watch } from "vue";
import type { AiAction } from "@/apis/ai.ts";
import type { AiCoachMessage } from "@/stores/aiCoach.ts";
import AiMessageItem from "@/components/ai/AiMessageItem.vue";

const props = defineProps<{
  messages: AiCoachMessage[];
  generating: boolean;
  /**
   * 需要业务处理的动作处理函数（judge_parse_save 等）。
   * 第二个参数是卡片所在的气泡：保存接口要的正文就是该 assistant 消息的 content，
   * 由 AiMessageItem 在调用时绑定后逐层传上来。
   */
  saveHandler?: (action: AiAction, message: AiCoachMessage) => Promise<void> | void;
}>();

const emit = defineEmits<{
  (e: "handled", action: AiAction): void;
}>();

const scrollRef = ref<HTMLDivElement | null>(null);
/** 用户是否贴在底部 */
const stickToBottom = ref(true);
const NEAR_BOTTOM_PX = 80;

function handleScroll() {
  const el = scrollRef.value;
  if (!el) {
    return;
  }
  stickToBottom.value = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_PX;
}

/** 滚到底部；force 用于浮窗刚打开时（此时尚未贴底判定） */
async function scrollToBottom(force = false) {
  if (!force && !stickToBottom.value) {
    return;
  }
  await nextTick();
  const el = scrollRef.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
}

// 消息增量与生成态变化都要跟随（thinking / content 都是逐片追加）
watch(
  () => props.messages,
  () => {
    void scrollToBottom();
  },
  { deep: true },
);

watch(
  () => props.generating,
  () => {
    void scrollToBottom();
  },
);

onMounted(() => {
  void scrollToBottom(true);
});

defineExpose({ scrollToBottom });
</script>

<template>
  <div ref="scrollRef" class="ai-message-list" @scroll="handleScroll">
    <div v-if="messages.length === 0" class="ai-message-empty">
      <t-icon name="chat" size="28px" />
      <p>我是 AI 教练，可以帮你分析提交、讲解题目思路。</p>
      <p class="ai-message-empty-hint">把你遇到的问题说清楚（题目、提交、现象）会更容易得到有用的回答。</p>
    </div>
    <AiMessageItem
      v-for="message in messages"
      :key="message.key"
      :message="message"
      :save-handler="saveHandler"
      @handled="(action: AiAction) => emit('handled', action)"
    />
  </div>
</template>

<style scoped>
.ai-message-list {
  flex: 1;
  /* 极矮窗口下至少留出一小块消息区，不被输入区全部挤没 */
  min-height: 96px;
  overflow-y: auto;
  padding: 12px;
  /* 滚动到底部时留一点余量，避免最后一条贴着输入区 */
  scroll-behavior: smooth;
}

.ai-message-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  min-height: 160px;
  padding: 16px;
  text-align: center;
  color: var(--td-text-color-placeholder);
}

.ai-message-empty p {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
}

.ai-message-empty-hint {
  font-size: 12px !important;
  color: var(--td-text-color-placeholder);
}
</style>
